import { Component, createRef } from 'preact';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { deckCardArt } from '../utils/sorcery/deckFeaturedCards';
import { resolveLocalImageUrl } from '../utils/localApi';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, INPUT_STYLE, RADIUS,
} from '../lib/medievalTheme';
import { isPhoneLayout } from '../utils/platform';
import { t } from '../localization/t';

// Phone: the panel renders in rem (device-absolute) instead of cqw -
// on the phone canvas 0.6cqw is a 5px label, far below legibility.
const PHONE = isPhoneLayout();

const PAD = 8;
const GAP = 6;

// Place the dropdown next to the slot it edits without ever leaving the
// canvas. `anchor` is a viewport rect (getBoundingClientRect); the panel
// renders `position: fixed` inside `.app-canvas`, which is a containing
// block (transform: translateZ(0)), so we convert to canvas-local
// coordinates by subtracting the canvas offset. Prefers below the slot,
// flips above when the bottom would overflow, then hard-clamps both
// axes so no edge can be cut off at any canvas size.
function placePanel(anchor, width, height) {
  if (!anchor) return null;
  const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
  const canvas = canvasEl
    ? canvasEl.getBoundingClientRect()
    : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

  const top = anchor.top - canvas.top;
  const bottom = anchor.bottom - canvas.top;
  const left = anchor.left - canvas.left;

  let y = bottom + GAP;
  if (y + height > canvas.height - PAD) {
    const above = top - GAP - height;
    y = above >= PAD ? above : canvas.height - PAD - height;
  }
  return {
    left: Math.max(PAD, Math.min(left, canvas.width - width - PAD)),
    top: Math.max(PAD, y),
  };
}

// Dropdown for one deck-tile showcase slot: search the cards in that
// deck and pick which art fills the slot.
export default class DeckShowcasePicker extends Component {
  state = { search: '', pos: null };
  rootRef = createRef();

  componentDidMount() {
    this._escId = pushDismiss(() => this.props.onClose?.());
    document.addEventListener('mousedown', this.handleOutsideDown, true);
    this.reposition();
  }

  componentDidUpdate() {
    this.reposition();
  }

  componentWillUnmount() {
    removeDismiss(this._escId);
    document.removeEventListener('mousedown', this.handleOutsideDown, true);
  }

  // Clicks on a slot are left alone so the gallery can toggle the
  // picker shut when the same slot is clicked again, and swap it to a
  // different slot otherwise. Anything else outside closes.
  handleOutsideDown = (e) => {
    const target = e.target;
    if (this.rootRef.current?.contains(target)) return;
    if (target?.closest?.('.deck-slot-target')) return;
    this.props.onClose?.();
  };

  // Measures the rendered panel, so a short filtered list sits snug
  // under the slot instead of being flipped by a worst-case estimate.
  reposition() {
    const el = this.rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = placePanel(this.props.anchor, rect.width, rect.height);
    const pos = this.state.pos;
    if (!next) return;
    if (pos && Math.abs(pos.left - next.left) < 0.5 && Math.abs(pos.top - next.top) < 0.5) return;
    this.setState({ pos: next });
  }

  getOptions() {
    const { deck, sorceryCards } = this.props;
    const seen = new Set();
    const options = [];
    for (const entry of deck?.cards || []) {
      if (!entry?.cardId || seen.has(entry.cardId)) continue;
      const art = deckCardArt(deck, entry.cardId, sorceryCards);
      if (!art) continue;
      seen.add(entry.cardId);
      options.push({ cardId: entry.cardId, ...art });
    }
    options.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return options;
  }

  render() {
    const { onPick, onReset, onClose } = this.props;
    const { search, pos } = this.state;
    const query = search.trim().toLowerCase();
    const options = this.getOptions().filter((o) => !query || o.name.toLowerCase().includes(query));

    return (
      <div
        ref={this.rootRef}
        className="fixed z-[60] flex flex-col"
        style={{
          left: pos ? pos.left : 0,
          top: pos ? pos.top : 0,
          // Hidden for the measuring pass only. One frame, no flash.
          visibility: pos ? 'visible' : 'hidden',
          width: PHONE ? '17rem' : '15cqw',
          maxHeight: PHONE ? '19rem' : '20cqw',
          background: '#0e0b08',
          border: `1px solid ${GOLD} 0.28)`,
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.75)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 p-2 shrink-0">
          <input
            type="text"
            autoFocus
            placeholder={t('Search deck...')}
            value={search}
            className="flex-1 min-w-0 px-2 py-1 outline-none"
            style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-sm)', color: TEXT_PRIMARY, fontSize: PHONE ? '0.875rem' : '0.6cqw' }}
            onInput={(e) => this.setState({ search: e.target.value })}
          />
          <button
            type="button"
            className="shrink-0"
            title={t('Use the default card')}
            style={{ color: TEXT_MUTED, fontSize: PHONE ? '0.75rem' : '0.55cqw' }}
            onClick={() => { onReset?.(); onClose?.(); }}
          >
            {t('Default')}
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pb-1">
          {options.map((o) => (
            <button
              key={o.cardId}
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1 text-left"
              style={{ color: TEXT_BODY, fontSize: PHONE ? '0.8125rem' : '0.6cqw', ...(PHONE ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' } : {}) }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.1)`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              onClick={() => { onPick?.(o.cardId); onClose?.(); }}
            >
              <img
                src={resolveLocalImageUrl(o.imageUrl)}
                alt=""
                draggable={false}
                className="shrink-0 object-cover"
                style={{ width: PHONE ? '2rem' : '1.6cqw', aspectRatio: '63 / 88', borderRadius: RADIUS.card }}
              />
              <span className="truncate">{o.name}</span>
            </button>
          ))}
          {options.length === 0 ? (
            <div className="px-2 py-3 text-center" style={{ color: TEXT_MUTED, fontSize: PHONE ? '0.75rem' : '0.55cqw' }}>
              {t('No cards match')}
            </div>
          ) : null}
        </div>

        <div
          className="shrink-0 px-2 py-1 text-center arena-heading"
          style={{ color: ACCENT_GOLD, fontSize: PHONE ? '0.6875rem' : '0.5cqw', borderTop: `1px solid ${GOLD} 0.18)` }}
        >
          {t('Pick a card')}
        </div>
      </div>
    );
  }
}

export { placePanel };
